import { Link } from 'react-router'
import { HEADER_WORDMARK } from '../../../../components/site-header/sizes'
import {
  gated,
  isPlaceholderPhone,
  isPlaceholderPiva,
  isPlaceholderText,
  isPlaceholderUrl,
} from '../../../../sedi/placeholders'
import type { SedeConfig } from '../../../../sedi/types'
import { displayPhone, telHref } from '../../../../lib/contact'
import { useCopy, useLocale } from '../../content'
import type { SiteCopy } from '../../content/types'
import { SERVICE_KEYS } from '../../data/services'
import { pathFor, type RouteDef } from '../../routes'
import { useSede } from '../../sede'
import { formatHours } from './hours'

type SocialKey = keyof SiteCopy['footer']['socials'] & keyof NonNullable<SedeConfig['socials']>

/** Display order of the social links; their text comes from `copy.footer.socials`. Google has its own «Recensioni» block. */
const SOCIAL_KEYS: readonly SocialKey[] = ['instagram', 'facebook', 'linkedin']

const LEGAL_LINKS: readonly [RouteDef['key'], keyof SiteCopy['footer']['links']][] = [
  ['privacy', 'privacy'],
  ['cookie', 'cookie'],
  ['dataDeletion', 'dataDeletion'],
  ['crediti', 'credits'],
]

const LABEL = 'vc2-kicker text-vc2-ink-soft'
const LINK = 'underline-offset-4 transition-colors hover:text-vc2-ink hover:underline'

/**
 * Site footer. Every company datum comes from the sede config and is rendered
 * only when it passes its placeholder gate: street, phone, PEC,
 * P.IVA, REA, hours and socials simply disappear until the owner fills them —
 * never a zero, never "TBD". The copyright line carries no year, so the
 * prerendered markup and the client render agree across a year boundary.
 */
export function Footer() {
  const copy = useCopy()
  const locale = useLocale()
  const sede = useSede()

  const legalName = gated(sede.legal?.legalName, isPlaceholderText) ?? copy.meta.legalName
  const street = gated(sede.contact.addressStreet, isPlaceholderText)
  const locality = gated(sede.contact.address, isPlaceholderText)
  const postal = gated(sede.contact.postalCode, isPlaceholderText)
  const email = gated(sede.contact.email, isPlaceholderText)
  const phone = gated(sede.contact.phone, isPlaceholderPhone)
  // `legal.*` first, `contact.*` as the fallback (the owner may fill either), each gated on its own.
  const pec = gated(sede.legal?.pec, isPlaceholderText) ?? gated(sede.contact.pec, isPlaceholderText)
  const piva = gated(sede.legal?.piva, isPlaceholderPiva) ?? gated(sede.contact.piva, isPlaceholderPiva)
  const rea = gated(sede.legal?.rea, isPlaceholderText)
  const hours = formatHours(sede.hours ?? [], locale)
  const socials = SOCIAL_KEYS.map((k) => ({ key: k, url: sede.socials?.[k] })).filter(
    (s): s is { key: SocialKey; url: string } => !isPlaceholderUrl(s.url),
  )
  const reviewsUrl = isPlaceholderUrl(sede.socials?.google) ? undefined : sede.socials?.google

  // "Via … n, 13100 Vercelli" once the street is real; `contact.address` alone until then.
  // `contact.address` may already carry street and CAP, so the town comes from the geo placename.
  const addressLine = street
    ? `${street}, ${postal ? `${postal} ` : ''}${sede.seo.geo.placename}`
    : locality
  const hasSchedule = hours.length > 0 || socials.length > 0 || !!reviewsUrl

  const bottomLine = [
    `© ${legalName}`,
    piva && `${copy.footer.pivaLabel} ${piva}`,
    rea && `${copy.footer.reaLabel} ${rea}`,
    copy.footer.rights,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <footer className="border-t border-vc2-hairline bg-vc2-surface text-vc2-ink text-sm">
      <div className="vc2-container grid gap-10 py-16 md:grid-cols-4">
        {/* 1 — brand */}
        <div>
          {/* Same logo + wordmark as the header; here the wordmark is always shown. */}
          <Link to={pathFor('home', locale)} className="inline-flex items-center gap-3">
            <img
              src="/media/verde-vercelli/logo.png"
              alt=""
              aria-hidden="true"
              draggable={false}
              className="h-10 w-auto select-none pointer-events-none shrink-0 drop-shadow-[0_2px_8px_rgba(201,168,117,0.3)]"
            />
            <span className={`vc2-display ${HEADER_WORDMARK}`}>Verde Vercelli</span>
          </Link>
          <p className="mt-3 text-vc2-ink-soft">{copy.meta.tagline}</p>
          <p className="mt-6">{legalName}</p>
          <ul className="mt-6 space-y-2 text-vc2-ink-soft">
            <li>
              <a href="https://verdevalsesia.it" className={LINK}>
                {copy.footer.network}
              </a>
            </li>
            <li>
              <a href="https://avmg.it" className={LINK}>
                {copy.footer.poweredBy}
              </a>
            </li>
          </ul>
        </div>

        {/* 2 — contact (NAP) */}
        <address className="space-y-5 not-italic">
          {addressLine && (
            <div>
              <p className={LABEL}>{copy.footer.addressLabel}</p>
              <p className="mt-1">{addressLine}</p>
            </div>
          )}
          {email && (
            <div>
              <p className={LABEL}>{copy.footer.emailLabel}</p>
              <a href={`mailto:${email}`} className={`mt-1 inline-block ${LINK}`}>
                {email}
              </a>
            </div>
          )}
          {phone && (
            <div>
              <p className={LABEL}>{copy.footer.phoneLabel}</p>
              <a href={telHref(phone)} className={`mt-1 inline-block ${LINK}`}>
                {displayPhone(phone)}
              </a>
            </div>
          )}
          {pec && (
            <div>
              <p className={LABEL}>{copy.footer.pecLabel}</p>
              <a href={`mailto:${pec}`} className={`mt-1 inline-block [overflow-wrap:anywhere] ${LINK}`}>
                {pec}
              </a>
            </div>
          )}
        </address>

        {/* 3 — hours, reviews and socials; the column is dropped while all are empty */}
        {hasSchedule && (
          <div className="space-y-5">
            {hours.length > 0 && (
              <div>
                <p className={LABEL}>{copy.footer.hoursLabel}</p>
                <ul className="mt-1 space-y-1">
                  {hours.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            )}
            {reviewsUrl && (
              <div>
                <p className={LABEL}>{copy.footer.reviewsLabel}</p>
                <a href={reviewsUrl} rel="noopener noreferrer me" target="_blank" className={`mt-1 inline-block ${LINK}`}>
                  {copy.footer.socials.google}
                </a>
              </div>
            )}
            {socials.length > 0 && (
              <ul className="space-y-1">
                {socials.map((s) => (
                  <li key={s.key}>
                    <a href={s.url} rel="noopener noreferrer me" target="_blank" className={LINK}>
                      {copy.footer.socials[s.key]}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* 4 — links; takes the empty third column's place until hours/socials exist */}
        <div className={hasSchedule ? '' : 'md:col-span-2'}>
          <nav aria-label={copy.a11y.footerNav} className={hasSchedule ? 'space-y-8' : 'grid gap-8 sm:grid-cols-2'}>
            <div>
              <p className={LABEL}>{copy.nav.services}</p>
              <ul className="mt-2 space-y-1.5">
                {SERVICE_KEYS.map((k) => (
                  <li key={k}>
                    <Link to={pathFor(k, locale)} className={LINK}>
                      {copy.pages[k].name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <ul className="space-y-1.5">
              <li>
                <Link to={pathFor('zone', locale)} className={LINK}>
                  {copy.nav.areas}
                </Link>
              </li>
              <li>
                <Link to={pathFor('contatti', locale)} className={LINK}>
                  {copy.nav.contact}
                </Link>
              </li>
              {/* Plain <a>: these are the shared legal section's static pages (src/legal/), not SPA routes. */}
              {LEGAL_LINKS.map(([key, label]) => (
                <li key={key} className={key === 'privacy' ? 'pt-4' : undefined}>
                  <a href={pathFor(key, locale)} className={`text-vc2-ink-soft ${LINK}`}>
                    {copy.footer.links[label]}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>

      <div className="border-t border-vc2-hairline">
        <p className="vc2-container py-6 text-xs text-vc2-ink-soft">{bottomLine}</p>
      </div>
    </footer>
  )
}
